import React from "react";
import { Helmet } from "react-helmet";
import Header from "../../Header";
import Footer from "../../Footer";
import "./ArVrGames.css";

export default function ArVrGames() {
    const solutions = [
        {
            title: "AR Game Development",
            description: "Location-based, marker-based, markerless, and camera-driven AR games with spatial interactions, 3D assets, rewards, and mobile-ready performance.",
            points: ["Marker & markerless AR", "Location-based play", "Mobile AR optimization"]
        },
        {
            title: "VR Game Development",
            description: "Immersive VR games and training experiences with hand tracking, controller support, spatial UI, optimized scenes, and headset-ready builds.",
            points: ["Headset-ready gameplay", "Controller interactions", "Comfort-focused design"]
        },
        {
            title: "Mixed Reality Games",
            description: "MR experiences that blend real and virtual worlds with spatial mapping, interaction design, 3D content, and device-specific optimization.",
            points: ["Spatial mapping", "Real-world interaction", "MR interface design"]
        },
        {
            title: "Immersive Multiplayer",
            description: "Shared AR/VR/MR experiences with real-time sync, rooms, avatars, voice/chat readiness, backend systems, analytics, and LiveOps support.",
            points: ["Shared sessions", "Avatar systems", "Backend integration"]
        }
    ];

    const capabilities = [
        "Unity AR/VR",
        "Unreal Engine",
        "AR Foundation",
        "ARCore",
        "ARKit",
        "WebXR",
        "Meta Quest",
        "VR Controllers",
        "Spatial UI",
        "3D Assets",
        "Optimization",
        "QA Testing",
        "Analytics",
        "Store Publishing",
        "LiveOps"
    ];

    const industries = [
        "Entertainment Games",
        "Educational Games",
        "Training Simulations",
        "Brand Experiences",
        "Sports & Fitness",
        "Real Estate",
        "Healthcare",
        "Product Demos"
    ];

    const process = [
        { step: "01", title: "Immersive Strategy", description: "We define target devices, AR/VR/MR mode, user flow, interaction model, content needs, and technical constraints." },
        { step: "02", title: "Prototype", description: "We validate controls, spatial UI, camera behavior, movement comfort, tracking quality, and gameplay loop." },
        { step: "03", title: "Production", description: "Our team builds environments, gameplay systems, 3D assets, UI, animations, backend integrations, and platform builds." },
        { step: "04", title: "Optimization & QA", description: "We test frame rate, device compatibility, tracking, controls, motion comfort, memory, loading, and release readiness." },
        { step: "05", title: "Launch & Support", description: "We support publishing, analytics, content updates, bug fixing, device improvements, and ongoing LiveOps." }
    ];

    const benefits = [
        { icon: "fa-solid fa-vr-cardboard", title: "Immersive Game Specialists", description: "We design AR, VR, and MR experiences around player comfort, interaction clarity, and device performance." },
        { icon: "fa-solid fa-cubes", title: "3D Art & Technical Art", description: "Our team supports environments, props, characters, shaders, lighting, VFX, optimization, and game-ready assets." },
        { icon: "fa-solid fa-server", title: "Backend Ready", description: "Add accounts, sessions, multiplayer rooms, analytics, admin tools, content systems, and live support." },
        { icon: "fa-solid fa-mobile-screen", title: "Device-Focused QA", description: "We test across mobile AR, headset VR, browser XR, and target hardware for stable launch builds." }
    ];

    const faqs = [
        {
            question: "What AR/VR game development services does Oengines provide?",
            answer: "Oengines provides AR, VR, MR, WebXR, Unity, Unreal, 3D asset production, spatial UI, multiplayer backend, QA, publishing, and LiveOps support."
        },
        {
            question: "Can you build AR games for Android and iOS?",
            answer: "Yes. We build mobile AR games using ARCore, ARKit, AR Foundation, Unity, and custom backend integrations when needed."
        },
        {
            question: "Can you build VR games for Meta Quest or other headsets?",
            answer: "Yes. We can develop headset-ready VR experiences with controller interactions, optimized 3D scenes, spatial UI, and release preparation."
        },
        {
            question: "Can AR/VR games include multiplayer?",
            answer: "Yes. We can add shared sessions, game rooms, player sync, avatars, voice/chat readiness, backend services, analytics, and live operations."
        }
    ];

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "AR and VR Game Development Services",
        "provider": {
            "@type": "Organization",
            "name": "Oengines Games LLP",
            "url": "https://www.oengines.com"
        },
        "serviceType": "AR VR MR Game Development",
        "areaServed": "Worldwide",
        "description": "AR, VR, and MR game development services for mobile AR, headset VR, WebXR, immersive multiplayer, 3D assets, QA, publishing, and live support.",
        "offers": solutions.map((solution) => ({
            "@type": "Offer",
            "itemOffered": {
                "@type": "Service",
                "name": solution.title,
                "description": solution.description
            }
        }))
    };

    return (
        <>
            <Helmet>
                <title>AR & VR Game Development Company | AR, VR, MR Games | Oengines</title>
                <meta name="description" content="Oengines Games LLP builds AR, VR, and MR games with Unity, Unreal, ARCore, ARKit, WebXR, 3D assets, spatial UI, multiplayer backend, QA, publishing, and LiveOps support." />
                <meta name="keywords" content="AR game development, VR game development, MR game development, AR VR game development company, Unity AR development, Unity VR development, WebXR game development, Meta Quest game development" />
                <link rel="canonical" href="https://www.oengines.com/ar-vr-games" />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
            </Helmet>
            <Header />
            <main className="arvr-page">
                <section className="arvr-hero">
                    <div className="container">
                        <div className="row align-items-center g-4">
                            <div className="col-lg-7">
                                <span className="arvr-eyebrow">AR, VR & MR Game Development</span>
                                <h1>Build Immersive AR & VR Games for Modern Platforms</h1>
                                <p>Oengines Games LLP creates immersive AR, VR, and MR games with spatial UI, 3D assets, optimized performance, multiplayer-ready backend systems, QA, publishing, and live product support.</p>
                                <div className="arvr-actions">
                                    <a href="/contact" className="as-btn">Start a Project <i className="fa-regular fa-arrow-right ms-2"></i></a>
                                    <a href="tel:+912614054978" className="arvr-call">Call: (0261) - 4054978</a>
                                </div>
                            </div>
                            <div className="col-lg-5">
                                <div className="arvr-hero-card">
                                    <img src="assets/img/GameDevelopment/services-vr-game-hero-1.jpg" alt="AR and VR game development" />
                                    <div>
                                        <strong>Immersive Game Production</strong>
                                        <span>AR, VR, MR, spatial UI, 3D assets, backend, optimization, and QA.</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="arvr-section space">
                    <div className="container">
                        <div className="arvr-heading text-center">
                            <span>Immersive Game Services</span>
                            <h2>AR, VR & Mixed Reality Game Development Services</h2>
                            <p>We build immersive products with the right balance of gameplay design, spatial interaction, technical art, backend engineering, device optimization, and launch support.</p>
                        </div>
                        <div className="row g-4">
                            {solutions.map((solution) => (
                                <div className="col-lg-3 col-md-6" key={solution.title}>
                                    <article className="arvr-card">
                                        <h3>{solution.title}</h3>
                                        <p>{solution.description}</p>
                                        <ul>
                                            {solution.points.map((point) => <li key={point}>{point}</li>)}
                                        </ul>
                                    </article>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="arvr-tech-section space">
                    <div className="container">
                        <div className="arvr-tech-panel">
                            <div className="arvr-heading mb-0">
                                <span>Tools & Capabilities</span>
                                <h2>Technology Coverage for Immersive Game Delivery</h2>
                                <p>We choose the engine, framework, device workflow, asset pipeline, and backend architecture based on your target experience and launch platform.</p>
                            </div>
                            <div className="arvr-tech-list">
                                {capabilities.map((item) => <span key={item}>{item}</span>)}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="arvr-industries-section space">
                    <div className="container">
                        <div className="arvr-heading text-center">
                            <span>Use Cases</span>
                            <h2>Immersive Experiences for Games, Training & Product Engagement</h2>
                        </div>
                        <div className="arvr-industries-list">
                            {industries.map((item) => <span key={item}>{item}</span>)}
                        </div>
                    </div>
                </section>

                <section className="arvr-process-section space">
                    <div className="container">
                        <div className="arvr-heading text-center">
                            <span>Development Process</span>
                            <h2>From Immersive Concept to Device-Ready Launch</h2>
                        </div>
                        <div className="arvr-process-grid">
                            {process.map((item) => (
                                <article className="arvr-process-card" key={item.step}>
                                    <span>{item.step}</span>
                                    <h3>{item.title}</h3>
                                    <p>{item.description}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="arvr-benefit-section space">
                    <div className="container">
                        <div className="arvr-heading text-center">
                            <span>Why Choose Oengines</span>
                            <h2>Immersive Game Development Backed by Full Production Support</h2>
                        </div>
                        <div className="row g-4">
                            {benefits.map((item) => (
                                <div className="col-lg-3 col-md-6" key={item.title}>
                                    <article className="arvr-benefit-card">
                                        <span><i className={item.icon}></i></span>
                                        <h3>{item.title}</h3>
                                        <p>{item.description}</p>
                                    </article>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="arvr-faq-section space">
                    <div className="container">
                        <div className="arvr-heading text-center">
                            <span>FAQ</span>
                            <h2>AR & VR Game Development Questions</h2>
                        </div>
                        <div className="arvr-faq-grid">
                            {faqs.map((item) => (
                                <article className="arvr-faq-card" key={item.question}>
                                    <h3>{item.question}</h3>
                                    <p>{item.answer}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="arvr-cta">
                    <div className="container">
                        <div className="arvr-cta-box">
                            <div>
                                <span>Build Immersive</span>
                                <h2>Plan your AR, VR, or MR game with Oengines.</h2>
                                <p>Share your target devices, gameplay idea, interaction model, and content needs. We will help define the right immersive production path.</p>
                            </div>
                            <a href="/contact" className="as-btn">Book Consultation <i className="fa-regular fa-arrow-right ms-2"></i></a>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
